import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenApi } from '../services/api';

interface TokenBalance {
  remaining: number;
  total_recharged: number;
  used: number;
  is_overdraft: boolean;
  overdraft_limit: number;
  warning_threshold_percent: number;
}

interface TokenAlertState {
  show: boolean;
  type: 'warning' | 'zero' | 'overdraft';
  title: string;
  message: string;
  context?: any; // 上下文信息，用于继续生成
  canContinue?: boolean; // 是否显示"继续生成"按钮（AI调用返回402时为true，页面检查余额时为false）
}

interface UseTokenCheckOptions {
  /** 是否启用欠费拦截（默认true，管理页面可设为false） */
  enableOverdraftBlock?: boolean;
  /** 继续生成回调（用户点击"继续生成"时触发） */
  onContinue?: () => void;
}

export const useTokenCheck = (options: UseTokenCheckOptions = {}) => {
  const { enableOverdraftBlock = true, onContinue } = options;
  const navigate = useNavigate();
  const [alert, setAlert] = useState<TokenAlertState>({
    show: false,
    type: 'warning',
    title: '',
    message: ''
  });
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  
  // 防重复弹框：记录当前会话是否已弹出过欠费警告弹框
  const hasShownOverdraftWarning = useRef(false);
  // 记录最后一次弹框的时间戳，用于防抖
  const lastAlertTime = useRef(0);

  // 检查 Token 余额
  const checkTokenBalance = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const response = await tokenApi.getBalance();
      const data = response.data;
      setBalance(data);

      const remaining = data.remaining || 0;
      const totalRecharged = data.total_recharged || 0;
      const used = data.used || 0;
      const isOverdraft = data.is_overdraft || false;
      const overdraftLimit = data.overdraft_limit || 0;

      // 计算使用百分比
      const usagePercent = totalRecharged > 0 ? (used / totalRecharged) * 100 : 0;
      const remainingPercent = totalRecharged > 0 ? (remaining / totalRecharged) * 100 : 100;

      // 1. 检查是否达到最大欠费额度（只在启用拦截时显示弹框）
      if (enableOverdraftBlock && isOverdraft && remaining <= -overdraftLimit) {
        setAlert({
          show: true,
          type: 'overdraft',
          title: '已达最大欠费额度',
          message: `您的 Token 已欠费 ${Math.abs(remaining).toLocaleString()}，已达最大欠费额度 ${overdraftLimit.toLocaleString()}，无法继续使用 AI 功能。请立即充值以恢复使用。`
        });
        return;
      }

      // 2. 检查 Token 是否已欠费（负数）（只在启用拦截时显示弹框）
      // 防重复弹框：同一会话内只弹出一次欠费警告
      if (enableOverdraftBlock && remaining < 0) {
        if (!hasShownOverdraftWarning.current) {
          hasShownOverdraftWarning.current = true;
          setAlert({
            show: true,
            type: 'zero',
            title: 'Token 已欠费',
            message: `您的 Token 已欠费 ${Math.abs(remaining).toLocaleString()}，欠费超过 ${overdraftLimit.toLocaleString()} 后将暂停 AI 生成功能，为不影响创作，请及时充值。`,
            canContinue: false // 页面检查余额触发的告警，不显示"继续生成"按钮
          });
        }
        // 欠费状态下直接返回，不关闭弹框
        return;
      }

      // 3. 检查 Token 是否为 0（只在启用拦截时显示弹框）
      if (enableOverdraftBlock && remaining === 0) {
        setAlert({
          show: true,
          type: 'zero',
          title: 'Token 已用完',
          message: `您的 Token 余额为 ${remaining.toLocaleString()}，为不影响创作，请立即充值。`
        });
        return;
      }

      // 4. 检查是否低于 10% 预警线（只在余额为正时显示，欠费状态不显示此提醒）
      if (remaining > 0 && remainingPercent <= 10) {
        setAlert({
          show: true,
          type: 'warning',
          title: 'Token 余额不足',
          message: `您的 Token 仅剩 ${remainingPercent.toFixed(1)}%（${remaining.toLocaleString()}），建议提前充值以避免创作中断。`
        });
        return;
      }

      // 余额充足，关闭弹窗
      // 但如果已经弹出过欠费警告（用户正在处理欠费），不要关闭弹窗
      if (!hasShownOverdraftWarning.current) {
        setAlert(prev => ({ ...prev, show: false }));
      }

    } catch (error) {
      console.error('检查 Token 余额失败:', error);
    }
  }, []);

  // 跳转到 Token 管理页面
  const handleRecharge = useCallback(() => {
    setAlert(prev => ({ ...prev, show: false }));
    navigate('/tokens?openRecharge=true');
  }, [navigate]);

  // 关闭弹窗
  const closeAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, show: false }));
  }, []);

  // 组件挂载时检查一次
  useEffect(() => {
    checkTokenBalance();
  }, [checkTokenBalance]);

  // 监听 Token 使用事件（AI调用后刷新余额）
  useEffect(() => {
    const handleTokenUsed = () => {
      // 延迟检查，等待后端扣减完成
      setTimeout(() => {
        checkTokenBalance();
      }, 500);
    };

    window.addEventListener('token:used', handleTokenUsed);
    return () => window.removeEventListener('token:used', handleTokenUsed);
  }, [checkTokenBalance]);

  // 监听欠费警告事件（402状态码，AI调用返回，欠费但未超过最大欠费额度）
  useEffect(() => {
    const handleWarning = (event: CustomEvent) => {
      if (!enableOverdraftBlock) return;
      
      // 防抖：3秒内不重复弹框
      const now = Date.now();
      if (now - lastAlertTime.current < 3000) return;
      lastAlertTime.current = now;
      
      const message = event.detail?.message || 'Token 已欠费';
      setAlert({
        show: true,
        type: 'zero',
        title: 'Token 已欠费',
        message: message,
        canContinue: true // AI调用返回402触发的告警，显示"继续生成"按钮
      });
      // 标记已弹出过欠费警告
      hasShownOverdraftWarning.current = true;
    };

    window.addEventListener('token:warning', handleWarning as EventListener);
    return () => window.removeEventListener('token:warning', handleWarning as EventListener);
  }, [enableOverdraftBlock]);

  // 监听欠费事件（API调用被拒绝时，400状态码，超过最大欠费额度）
  useEffect(() => {
    const handleOverdraft = (event: CustomEvent) => {
      // 如果未启用拦截，只刷新余额但不显示弹框
      if (!enableOverdraftBlock) {
        checkTokenBalance();
        return;
      }
      const message = event.detail?.message || 'Token 余额不足';
      setAlert({
        show: true,
        type: 'overdraft',
        title: '已达最大欠费额度',
        message: message + '，无法继续使用 AI 功能。请立即充值以恢复使用。'
      });
      // 刷新余额显示
      checkTokenBalance();
    };

    window.addEventListener('token:overdraft', handleOverdraft as EventListener);
    return () => window.removeEventListener('token:overdraft', handleOverdraft as EventListener);
  }, [checkTokenBalance, enableOverdraftBlock]);

  // 继续生成（关闭弹框并触发事件）
  const handleContinue = useCallback(() => {
    setAlert(prev => ({ ...prev, show: false }));
    // 触发 token:continue 事件，通知其他组件继续生成
    window.dispatchEvent(new CustomEvent('token:continue'));
    if (onContinue) {
      onContinue();
    }
  }, [onContinue]);

  return {
    alert,
    balance,
    checkTokenBalance,
    handleRecharge,
    closeAlert,
    handleContinue
  };
};

export default useTokenCheck;
